import { Component } from '@builder.io/sdk';

/**
 * Builder panel for the blog Calculator.
 *
 * Every calculator in the Integration Health series is configured here, with no
 * developer involvement per post. The defaults ship the Integration Uptime
 * example, so a freshly dropped-in calculator already works and an editor can
 * see what each field does before changing anything.
 *
 * The formula itself is printed in the post copy above the calculator, per Bru,
 * so the component deliberately does not render it.
 */
const calculatorFields = [
  {
    name: 'heading',
    type: 'string',
    defaultValue: 'Integration Uptime',
    helperText: 'Calculator title.',
  },
  {
    name: 'supportingText',
    type: 'string',
    defaultValue: 'Enter one total per Σ.',
    helperText: 'Optional line under the heading. Leave empty to hide it.',
  },
  {
    name: 'inputs',
    type: 'list',
    helperText: 'The fields a reader fills in. Every field is required for the calculator to show a result.',
    defaultValue: [
      { key: 'successful', label: 'Σ Successful execution time', defaultValue: 8640, suffixLabel: 'hrs' },
      { key: 'scheduled', label: 'Σ Total scheduled execution time', defaultValue: 8760, suffixLabel: 'hrs' },
    ],
    subFields: [
      {
        name: 'key',
        type: 'string',
        required: true,
        helperText:
          'The name formulas use for this field. Letters, numbers and underscores only, no spaces. Renaming it breaks every formula that refers to it.',
      },
      { name: 'label', type: 'string', required: true, helperText: 'Shown above the field.' },
      {
        name: 'defaultValue',
        type: 'number',
        helperText: 'The value the field starts with. Pick a realistic figure, since readers see it before they type.',
      },
      {
        name: 'prefixLabel',
        type: 'string',
        helperText: "Shown at the start of the field, e.g. '$'. Display only, never part of the maths.",
      },
      {
        name: 'suffixLabel',
        type: 'string',
        helperText: "Shown at the end of the field, e.g. 'hrs', 'days', '%'. Display only, never part of the maths.",
      },
    ],
  },
  {
    name: 'results',
    type: 'list',
    helperText:
      'What the calculator works out. Results compute in order, and a later formula can use an earlier result by its key.',
    defaultValue: [
      {
        key: 'uptime',
        label: 'Integration Uptime',
        formula: '(successful / scheduled) * 100',
        valueType: 'percent',
        decimals: '2',
      },
    ],
    subFields: [
      {
        name: 'key',
        type: 'string',
        required: true,
        helperText:
          'The name later formulas use for this result. Must differ from every input key and every other result key.',
      },
      { name: 'label', type: 'string', required: true, helperText: 'Shown beside the figure.' },
      {
        name: 'formula',
        type: 'string',
        required: true,
        helperText:
          "Written with input keys, e.g. '(successful / scheduled) * 100'. Supports + - * / and brackets, plus round(), abs(), min() and max(). You can paste the × and ÷ signs straight from the formula doc.",
      },
      {
        name: 'valueType',
        type: 'string',
        enum: ['number', 'percent', 'currency'],
        defaultValue: 'number',
        helperText: "'percent' adds a % sign, 'currency' formats as dollars. Use 'number' for everything else.",
      },
      {
        name: 'decimals',
        type: 'string',
        enum: ['auto', '0', '1', '2', '3'],
        defaultValue: 'auto',
        helperText: "'auto' shows up to two decimals and drops trailing zeros, so 43.5 stays 43.5 rather than 43.50.",
      },
      {
        name: 'suffixLabel',
        type: 'string',
        helperText: "Unit after the figure, e.g. 'days', 'hours', 'pp', 'per month', 'per 100 instances'.",
      },
      {
        name: 'showSign',
        type: 'boolean',
        defaultValue: false,
        helperText:
          'Put a + in front of a positive figure. Worth turning on where the direction is the point, such as a lift or a burn rate.',
      },
    ],
  },
] as const;

const calculatorConfig = {
  name: 'Calculator',
  inputs: calculatorFields,
} as Component;

export type CalculatorConfigProps = GenerateBuilder<typeof calculatorFields>;

export default calculatorConfig;
